import { useTranslation } from "react-i18next";
import { useCallback, useMemo, useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";

export const ImageCardDropdownMaterial = ({
  materialList,
  materialSelectRef,
  materialsToDisplay,
  setMaterialsToDisplay,
  className = "",
  dropdownContainerRef = null,
  emptySelectionLabelKey = "select_materials",
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const [buttonWidth, setButtonWidth] = useState(null);

  const totalCount = (materialList || []).reduce((sum, m) => {
    if (!m.value) return sum;
    if (
      m.value === "materials_with_review_cls" ||
      m.value === "materials_with_comment" ||
      m.value === "materials_with_demand_delete"
    ) {
      return sum;
    }
    return sum + (m.count ?? 0);
  }, 0);

  const listOptions = [
    ...((materialList || []).length > 0
      ? [
          {
            value: "all",
            name: t("all_materials"),
            count: totalCount,
            color: "#FFFFFF",
          },
        ]
      : []),
    ...(materialList || []),
  ].map((m) => ({
    ...m,
    nameAndCount: `${m.name} (${m.count})`,
  }));

  const allMaterialValues = useMemo(
    () =>
      (materialList || [])
        .filter((m) => {
          if (!m.value) return false;
          return ![
            "materials_with_review_cls",
            "materials_with_comment",
            "materials_with_demand_delete",
          ].includes(m.value);
        })
        .map((m) => m.value.toLowerCase()),
    [materialList],
  );

  const isAllSelected = useMemo(() => {
    if (materialsToDisplay.length === 0) return false;
    if (allMaterialValues.length === 0) return false;
    // Vérifier que tous les matériaux de la liste sont sélectionnés
    // (pas nécessairement que le nombre est égal, car materialsToDisplay peut contenir des valeurs supplémentaires)
    return allMaterialValues.every((val) => materialsToDisplay.includes(val));
  }, [materialsToDisplay, allMaterialValues]);

  const selectedValues = useMemo(() => {
    if (isAllSelected) {
      // Si tous les matériaux sont sélectionnés, inclure "all" et tous les matériaux individuels
      const allOption = listOptions.find((opt) => opt.value === "all");
      const individualMaterials = listOptions.filter((opt) => {
        if (opt.value === "all") return false;
        // Inclure tous les matériaux qui sont dans allMaterialValues
        return allMaterialValues.includes(opt.value?.toLowerCase());
      });
      // S'assurer que allOption existe avant de l'inclure
      return allOption
        ? [allOption, ...individualMaterials]
        : individualMaterials;
    }
    // Sinon, retourner uniquement les matériaux individuels sélectionnés
    return listOptions.filter((opt) => {
      if (opt.value === "all") return false;
      return materialsToDisplay.includes(opt.value?.toLowerCase());
    });
  }, [isAllSelected, materialsToDisplay, listOptions, allMaterialValues]);

  const updateSelectedMaterials = useCallback(
    (selectedList) => {
      const values = selectedList.map((item) => item.value);
      const selectedMaterialValues = values
        .filter((v) => v !== "all")
        .map((v) => v.toLowerCase());

      // Si "all" est dans la sélection, cela signifie qu'on vient de cocher "all_materials"
      // ou qu'on a coché tous les matériaux individuellement
      if (values.includes("all")) {
        // Cocher tous les matériaux
        setMaterialsToDisplay([...allMaterialValues]);
        return;
      }

      // Si "all" n'est pas dans la sélection, on gère les matériaux individuels
      // Si tous les matériaux individuels sont sélectionnés, on devrait avoir "all" aussi
      // mais comme il n'est pas dans la liste, on vérifie manuellement
      const allIndividuallySelected =
        selectedMaterialValues.length === allMaterialValues.length &&
        allMaterialValues.every((val) => selectedMaterialValues.includes(val));

      if (allIndividuallySelected) {
        // Tous les matériaux sont sélectionnés individuellement, on les garde tous
        setMaterialsToDisplay([...allMaterialValues]);
        return;
      }

      // Sinon, on utilise la sélection actuelle (qui peut être vide ou partielle)
      setMaterialsToDisplay(selectedMaterialValues);
    },
    [setMaterialsToDisplay, allMaterialValues],
  );

  // Get display text
  const displayText = useMemo(() => {
    if (materialsToDisplay.length === 0) {
      return t(emptySelectionLabelKey);
    }
    if (isAllSelected) {
      return t("all_materials");
    }
    if (materialsToDisplay.length === 1) {
      const material = (materialList || []).find(
        (m) => m.value?.toLowerCase() === materialsToDisplay[0],
      );
      if (material?.name) return material.name;
      // Keep label localized even when selected material
      // does not exist in the currently displayed image.
      return t(materialsToDisplay[0]) || materialsToDisplay[0];
    }
    return t("materials_selected", { count: materialsToDisplay.length });
  }, [
    materialsToDisplay,
    isAllSelected,
    materialList,
    t,
    emptySelectionLabelKey,
  ]);

  // Determine if option should show color dot
  const shouldShowColorDot = (option) => {
    // Show color dot for all materials except "all" and special materials
    if (option.value === "all" || !option.color) return false;
    // Show dot for regular materials (even if color is white/light)
    return ![
      "materials_with_review_cls",
      "materials_with_comment",
      "materials_with_demand_delete",
    ].includes(option.value);
  };

  // Measure button width when it changes
  useEffect(() => {
    if (buttonRef.current) {
      const updateWidth = () => {
        if (buttonRef.current) {
          setButtonWidth(buttonRef.current.offsetWidth);
        }
      };
      updateWidth();
      // Update on window resize
      window.addEventListener("resize", updateWidth);
      return () => window.removeEventListener("resize", updateWidth);
    }
  }, [displayText, isOpen]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current?.contains(event.target) ?? false;
      const inPortal =
        dropdownContainerRef?.current?.contains(event.target) ?? false;
      if (!inTrigger && !inPortal) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen, dropdownContainerRef]);

  // Expose reset method to ref
  useEffect(() => {
    if (materialSelectRef) {
      const resetMethod = {
        resetSelectedValues: () => {
          setMaterialsToDisplay([]);
          setIsOpen(false);
        },
      };

      if (typeof materialSelectRef === "function") {
        materialSelectRef(resetMethod);
      } else if (materialSelectRef && typeof materialSelectRef === "object") {
        materialSelectRef.current = resetMethod;
      }
    }
  }, [materialSelectRef, setMaterialsToDisplay]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleSelectOption = (option) => {
    const currentSelected = selectedValues.map((v) => v.value);
    const isCurrentlySelected = currentSelected.includes(option.value);

    let newSelected;
    if (option.value === "all") {
      if (isAllSelected) {
        // Décocher "all_materials" : décocher toutes les cases
        newSelected = [];
      } else {
        // Cocher "all_materials" : cocher toutes les cases individuelles
        newSelected = listOptions.filter((opt) => opt.value !== "all");
      }
    } else {
      // Gestion des cases individuelles
      if (isCurrentlySelected) {
        // Décocher une case individuelle
        // Retirer le matériau de la sélection
        newSelected = selectedValues.filter((v) => v.value !== option.value);
        // Si "all" était dans la sélection, le retirer aussi
        // car tous les matériaux ne sont plus sélectionnés
        newSelected = newSelected.filter((v) => v.value !== "all");
      } else {
        // Cocher une case individuelle
        newSelected = [...selectedValues, option];
        // Retirer "all" de la sélection si elle était présente
        // car on gère maintenant les matériaux individuellement
        newSelected = newSelected.filter((v) => v.value !== "all");
        // Si toutes les cases individuelles sont maintenant cochées, cocher aussi "all_materials"
        const individualSelected = newSelected;
        if (individualSelected.length === allMaterialValues.length) {
          const allOption = listOptions.find((opt) => opt.value === "all");
          if (allOption) {
            newSelected = [allOption, ...individualSelected];
          }
        }
      }
    }

    updateSelectedMaterials(newSelected);
  };

  return (
    <div
      className={`relative flex-1 min-w-[200px] bg-white rounded-full ${className}`}
      ref={dropdownRef}
    >
      {/* Custom styled button */}
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="w-full flex items-center justify-between bg-transparent outline-none cursor-pointer py-1.5 px-2 h-8 text-left"
      >
        <span className="truncate flex-1 text-gray-900 font-medium">
          {displayText}
        </span>
        <svg
          className={`w-4 h-4 text-gray-400 flex-shrink-0 ml-2 transition-transform ${
            isOpen ? "" : "transform rotate-180"
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown menu - portaled to dropdownContainerRef when provided so left edge aligns with full "Matériaux :" block */}
      {isOpen &&
        (dropdownContainerRef?.current ? (
          createPortal(
            <div
              className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
              style={{
                width: buttonWidth ? `${buttonWidth}px` : "100%",
                minWidth: "280px",
              }}
            >
              <div className="py-1">
                {listOptions.map((option, index) => {
                  const isSelected = selectedValues.some(
                    (v) => v.value === option.value,
                  );
                  const showColorDot = shouldShowColorDot(option);
                  return (
                    <div
                      key={option.value || index}
                      onClick={() => handleSelectOption(option)}
                      className={`group flex items-center px-2 py-2.5 cursor-pointer transition-colors ${
                        isSelected
                          ? "bg-gray-50 hover:bg-[#2AB9B8]"
                          : "hover:bg-[#2AB9B8]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="w-4 h-4 mr-2 rounded focus:ring-[#2AB9B8] cursor-pointer flex-shrink-0 border-gray-300 text-[#2AB9B8] group-hover:border-white"
                        style={{ accentColor: "#2AB9B8" }}
                      />
                      <span className="flex-1 text-sm font-medium text-gray-900 group-hover:text-white">
                        {option.nameAndCount || option.name}
                      </span>
                      {showColorDot && option.color && (
                        <div
                          className="w-3 h-3 rounded-full ml-2 border border-gray-300 flex-shrink-0"
                          style={{ backgroundColor: option.color }}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>,
            dropdownContainerRef.current,
          )
        ) : (
          <div
            className="absolute top-full right-0 mt-2 ml-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
            style={{
              width: buttonWidth ? `${buttonWidth}px` : "100%",
              minWidth: "280px",
            }}
          >
            <div className="py-1">
              {listOptions.map((option, index) => {
                const isSelected = selectedValues.some(
                  (v) => v.value === option.value,
                );
                const showColorDot = shouldShowColorDot(option);
                return (
                  <div
                    key={option.value || index}
                    onClick={() => handleSelectOption(option)}
                    className={`group flex items-center px-2 py-2.5 cursor-pointer transition-colors ${
                      isSelected
                        ? "bg-gray-50 hover:bg-[#2AB9B8]"
                        : "hover:bg-[#2AB9B8]"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => {}}
                      className="w-4 h-4 mr-2 rounded focus:ring-[#2AB9B8] cursor-pointer flex-shrink-0 border-gray-300 text-[#2AB9B8] group-hover:border-white"
                      style={{ accentColor: "#2AB9B8" }}
                    />
                    <span className="flex-1 text-sm font-medium text-gray-900 group-hover:text-white">
                      {option.nameAndCount || option.name}
                    </span>
                    {showColorDot && option.color && (
                      <div
                        className="w-3 h-3 rounded-full ml-2 border border-gray-300 flex-shrink-0"
                        style={{ backgroundColor: option.color }}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
    </div>
  );
};
